import { Controller } from "@hotwired/stimulus"
import { track } from "../lib/mixpanel"

// Pre-construction steps 4–6 (Level & carry, Main budget, Package for
// owner), the prototype's behaviour. Attached on each of those panels (and
// on a step-4 pane in the By cost code view). Talks to the page's `precon`
// controller for JSON, panel swaps, toasts and pop-ups. A panel's HTML is
// replaced after every save, so what the person was looking at (list
// grouping, filter, open package, As sent / Leveled, budget view, open
// rows, compared version) lives in STATE and comes back on connect.
const STATE = {}
const SPLIT_KEY = "conslog.precon.listWidth"
const GROUP_KEY = "conslog.precon.s4Group"
const VIEW_KEY = "conslog.precon.budgetView"

export default class extends Controller {
  static targets = ["level", "leftlist", "splitbar", "none", "nextBtn", "levqs"]
  static values = { step: Number, strings: Object }

  // ── Shared ────────────────────────────────────────────────────────────
  get p() {
    const el = this.element.closest("[data-controller~='precon']")
    return el ? this.application.getControllerForElementAndIdentifier(el, "precon") : null
  }

  get st() {
    const id = this.p?.projectIdValue || 0
    STATE[id] ||= { group: null, filter: "all", pkg: null, mode: {}, view: null, open: {}, bv: null, bvSame: false, s6: {} }
    return STATE[id]
  }

  get base() { return this.p?.baseUrlValue || "" }

  _t(key, vars = {}) {
    let s = (this.stringsValue && this.stringsValue[key]) || key
    Object.entries(vars).forEach(([k, v]) => { s = s.replaceAll(`%{${k}}`, v) })
    return s
  }

  _toast(msg, type) { this.p?._showToast(msg, type) }

  _ls(key, value) {
    try {
      if (value === undefined) return window.localStorage.getItem(key)
      window.localStorage.setItem(key, value)
    } catch (e) { /* no storage */ }
    return null
  }

  // POST/PATCH/DELETE, then swap what came back. On failure: undo() and an error toast.
  async _send(method, path, body, { undo, ok, quiet, keep } = {}) {
    try {
      const q = this.p?._viewQuery
      const url = `${this.base}/${path}${q && !quiet ? (path.includes("?") ? "&" : "?") + q : ""}`
      const data = await this.p._json(method, url, body)
      if (keep !== undefined && data?.panels?.["4"]) this._keepPanes(keep)
      if (!quiet) this.p._applyResponse(data)
      if (ok) this._toast(typeof ok === "function" ? ok(data) : ok)
      return data
    } catch (e) {
      if (undo) undo()
      this._toast(e.message || this._t("error"), "error")
      return null
    }
  }

  _num(v) { const n = parseFloat(String(v ?? "").replace(/[^\d.\-]/g, "")); return isNaN(n) ? 0 : n }
  _money(n) { const v = Math.round(n); return (v < 0 ? "−$" : "$") + Math.abs(v).toLocaleString("en-US") }
  _esc(s) { return String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])) }

  connect() {
    this._esc_ = (e) => { if (e.key === "Escape") this._closeAll() }
    document.addEventListener("keydown", this._esc_)
    if (this.stepValue === 4) this._restore4()
    if (this.stepValue === 5) this._restore5?.()
    if (this.stepValue === 6) this._restore6?.()
  }

  disconnect() { document.removeEventListener("keydown", this._esc_) }

  stop(e) { e.stopPropagation() }
  blurField(e) { e.preventDefault(); e.currentTarget.blur() }

  closeModal(e) {
    const overlay = e.currentTarget.closest(".overlay") || e.currentTarget
    overlay.classList.add("hidden")
  }

  _closeAll() { this.element.querySelectorAll(".overlay[data-s4-modal]:not(.hidden), .overlay[data-s5-modal]:not(.hidden), .overlay[data-s6-modal]:not(.hidden)").forEach(o => o.classList.add("hidden")) }

  _modal(name) {
    return this.element.querySelector(`[data-s4-modal='${name}'], [data-s5-modal='${name}'], [data-s6-modal='${name}']`) ||
      this.element.closest("[data-precon-target='panel']")?.querySelector(`[data-s4-modal='${name}'], [data-s5-modal='${name}'], [data-s6-modal='${name}']`)
  }

  // A small prototype pop-up with a text box (ask a sub, name a line): never window.prompt.
  _ask(title, message, value, okLabel, onOk, { amount = false } = {}) {
    const wrap = document.createElement("div")
    wrap.className = "overlay"
    wrap.innerHTML = `<div class="modal" role="dialog"><div class="mhead"><h2 class="cardtitle"></h2><button class="x" type="button" data-no aria-label="${this._esc(this._t("close"))}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"/></svg></button></div>
      <div class="pad col12"><span class="muted" data-msg></span>${amount ? `<input class="inp" type="text" data-text><span class="money"><span>$</span><input class="inp" type="text" inputmode="decimal" data-amount style="text-align:right"></span>` : `<textarea class="inp ta" rows="4" data-text></textarea>`}</div>
      <div class="mfoot"><button type="button" class="btn btn-secondary" data-no></button><button type="button" class="btn btn-primary" data-yes></button></div></div>`
    wrap.querySelector("h2").textContent = title
    wrap.querySelector("[data-msg]").textContent = message
    const text = wrap.querySelector("[data-text]")
    const amt = wrap.querySelector("[data-amount]")
    text.value = value || ""
    if (amount) text.placeholder = this._t("line_name_ph")
    wrap.querySelector(".mfoot [data-no]").textContent = this._t("cancel")
    wrap.querySelector("[data-yes]").textContent = okLabel
    const close = () => wrap.remove()
    wrap.addEventListener("click", (e) => { if (e.target === wrap) close() })
    wrap.querySelectorAll("[data-no]").forEach(b => b.addEventListener("click", close))
    wrap.querySelector("[data-yes]").addEventListener("click", () => {
      const v = text.value.trim()
      if (!v) { text.focus(); return }
      if (amount && !this._num(amt.value)) { amt.focus(); return }
      close(); onOk(v, amt ? amt.value : null)
    })
    ;(this.p?.element || document.body).appendChild(wrap)
    text.focus()
  }

  // ── Step 4: the list ──────────────────────────────────────────────────
  _restore4() {
    const st = this.st
    const w = parseInt(this._ls(SPLIT_KEY), 10)
    if (this.hasLeftlistTarget && w >= 220 && w <= 640) this._setSplit(w)
    if (this.hasLevelTarget) {
      const saved = st.group || (this._ls(GROUP_KEY) === "pkg" ? "package" : null)
      if (saved) this._group(saved)
      this._filter(st.filter || "all")
      const cur = this.element.querySelector("[data-precon-target='packageRow'].sel")?.dataset.packageId
      if (st.pkg && st.pkg !== cur && this._pane(st.pkg)) this._select(st.pkg, false)
    }
    this.element.querySelectorAll("[data-precon-target='packagePanel']").forEach(pane => {
      if (st.mode[pane.dataset.packageId] === "leveled") this._mode(pane, "leveled")
    })
    this._loadPanes()
  }

  // Speed: the panel comes with the open package only. The others come back
  // from before (when an action here changed just the open one) or load in
  // one background request, so switching packages is instant.
  _loadPanes() {
    const pending = Array.from(this.element.querySelectorAll("[data-precon-target='packagePanel'][data-pending='1']"))
    if (!pending.length) return
    const keep = this.st.keep
    this.st.keep = null
    const need = []
    pending.forEach(ph => {
      const id = ph.dataset.packageId
      const old = keep && keep.panes[id]
      if (old && id !== String(keep.except)) this._placePane(ph, old)
      else need.push(id)
    })
    if (!need.length || !this.p) return
    this._panesReq = this.p._json("GET", `${this.base}/packages/panes?ids=${need.join(",")}`).then(data => {
      Object.entries(data.panes || {}).forEach(([id, html]) => {
        const ph = this.element.querySelector(`[data-precon-target='packagePanel'][data-package-id='${id}'][data-pending='1']`)
        if (!ph) return
        const tmp = document.createElement("div")
        tmp.innerHTML = html.trim()
        const pane = tmp.firstElementChild
        if (pane) this._placePane(ph, pane)
      })
    }).catch(() => {})
  }

  _placePane(ph, pane) {
    const id = ph.dataset.packageId
    pane.classList.toggle("hidden", ph.classList.contains("hidden"))
    ph.replaceWith(pane)
    this._mode(pane, this.st.mode[id] === "leveled" ? "leveled" : "sent")
  }

  // Before an action on one package swaps the panel: keep the other panes.
  _keepPanes(exceptId) {
    const panes = {}
    this.element.querySelectorAll("[data-precon-target='packagePanel']:not([data-pending])").forEach(p => { panes[p.dataset.packageId] = p })
    this.st.keep = { except: String(exceptId), panes }
  }

  _pane(id) { return this.element.querySelector(`[data-precon-target='packagePanel'][data-package-id='${id}']`) }

  selectPackage(e) { this._select(e.currentTarget.dataset.packageId, true) }

  _select(id, scroll) {
    id = String(id)
    this.st.pkg = id
    if (this.p) this.p._selectedPackage = id
    this.element.querySelectorAll("[data-precon-target='packageRow']").forEach(r => r.classList.toggle("sel", r.dataset.packageId === id))
    this.element.querySelectorAll("[data-precon-target='packagePanel']").forEach(p => p.classList.toggle("hidden", p.dataset.packageId !== id))
    this._paintNext(id)
    if (scroll) {
      const pane = this._pane(id)
      const top = pane?.closest(".split")?.getBoundingClientRect().top
      if (top !== undefined && top < 0) window.scrollBy({ top: top - 12 })
    }
  }

  // "Next to pick": the next package in the list that nothing is carried on.
  _paintNext(id) {
    if (!this.hasNextBtnTarget) return
    const seen = []
    const rows = Array.from(this.element.querySelectorAll("[data-list='division'] [data-precon-target='packageRow']"))
    rows.forEach(r => { if (!seen.find(x => x.dataset.packageId === r.dataset.packageId)) seen.push(r) })
    const i = seen.findIndex(r => r.dataset.packageId === id)
    let next = null
    for (let k = 1; k <= seen.length; k++) {
      const r = seen[(i + k + seen.length) % seen.length]
      if (r && !r.dataset.filters.split(" ").includes("done") && !r.dataset.filters.split(" ").includes("allow")) { next = r; break }
    }
    const btn = this.nextBtnTarget
    btn.disabled = !next
    btn.dataset.packageId = next ? next.dataset.packageId : ""
    const label = btn.querySelector("span")
    if (label) label.textContent = next ? this._t("next_to_pick", { code: next.querySelector(".lcode")?.textContent || "", name: next.querySelector(".lname")?.textContent || "" }) : this._t("all_carried")
  }

  nextToPick(e) { const id = e.currentTarget.dataset.packageId; if (id) this._select(id, true) }

  groupList(e) {
    const g = e.currentTarget.dataset.group
    this._group(g)
    this._ls(GROUP_KEY, g === "package" ? "pkg" : "div")
    track("Precon Level List Grouped", { project_id: this.p?.projectIdValue, group: g })
  }

  _group(g) {
    this.st.group = g
    this.element.querySelectorAll(".s4grp .segbtn").forEach(b => { const on = b.dataset.group === g; b.classList.toggle("on", on); b.setAttribute("aria-pressed", on) })
    this.element.querySelectorAll("[data-list]").forEach(l => l.classList.toggle("hidden", l.dataset.list !== g))
    this._filter(this.st.filter || "all")
  }

  filterList(e) { this._filter(e.currentTarget.dataset.filter) }

  _filter(f) {
    this.st.filter = f
    const chips = this.element.querySelectorAll(".s4f .fchip")
    chips.forEach(c => {
      const on = c.dataset.filter === f
      c.classList.toggle("on", on)
      c.setAttribute("aria-pressed", on)
      c.classList.toggle("hidden", !(c.dataset.filter === "all" || on || parseInt(c.dataset.count, 10) > 0))
    })
    let any = false
    this.element.querySelectorAll("[data-list]").forEach(list => {
      let head = null
      let headHas = false
      const close = () => { if (head) head.classList.toggle("hidden", !headHas) }
      Array.from(list.children).forEach(el => {
        if (el.hasAttribute("data-group-head")) { close(); head = el; headHas = false; return }
        const show = el.dataset.filters.split(" ").includes(f)
        el.classList.toggle("hidden", !show)
        if (show) { headHas = true; if (!list.classList.contains("hidden")) any = true }
      })
      close()
    })
    if (this.hasNoneTarget) this.noneTarget.classList.toggle("hidden", any)
  }

  // ── Step 4: the list's width (drag the bar; double-click resets) ──────
  _setSplit(w) {
    w = Math.round(Math.min(640, Math.max(220, w)))
    this.leftlistTarget.style.width = `${w}px`
    if (this.hasSplitbarTarget) this.splitbarTarget.setAttribute("aria-valuenow", w)
    return w
  }

  splitDown(e) {
    if (e.button !== undefined && e.button !== 0) return
    e.preventDefault()
    const bar = e.currentTarget
    const box = bar.parentElement
    const maxW = Math.max(220, Math.min(640, Math.round(box.getBoundingClientRect().width * 0.6)))
    const startX = e.clientX
    const startW = this.leftlistTarget.getBoundingClientRect().width
    bar.classList.add("drag")
    const move = (ev) => this._setSplit(Math.min(maxW, startW + ev.clientX - startX))
    const up = () => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); window.removeEventListener("pointercancel", up)
      document.body.style.cursor = ""; document.body.style.userSelect = ""
      bar.classList.remove("drag")
      this._ls(SPLIT_KEY, String(Math.round(this.leftlistTarget.getBoundingClientRect().width)))
    }
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up); window.addEventListener("pointercancel", up)
    document.body.style.cursor = "col-resize"; document.body.style.userSelect = "none"
  }

  splitReset() { this._ls(SPLIT_KEY, String(this._setSplit(340))) }

  splitKey(e) {
    const step = e.shiftKey ? 48 : 16
    const w = this.leftlistTarget.getBoundingClientRect().width
    if (e.key === "ArrowLeft") { e.preventDefault(); this._ls(SPLIT_KEY, String(this._setSplit(w - step))) }
    if (e.key === "ArrowRight") { e.preventDefault(); this._ls(SPLIT_KEY, String(this._setSplit(w + step))) }
  }

  // ── Step 4: a package's pane ──────────────────────────────────────────
  levelMode(e) {
    const pane = e.currentTarget.closest("[data-precon-target='packagePanel']")
    const mode = e.currentTarget.dataset.mode
    this._mode(pane, mode)
    this.st.mode[pane.dataset.packageId] = mode
    track("Precon Level Mode Switched", { project_id: this.p?.projectIdValue, mode, package_id: pane.dataset.packageId })
  }

  _mode(pane, mode) {
    if (!pane) return
    pane.dataset.mode = mode
    pane.querySelectorAll(".levopt").forEach(b => { const on = b.dataset.mode === mode; b.classList.toggle("on", on); b.setAttribute("aria-pressed", on) })
    pane.querySelectorAll("[data-sent-only]").forEach(el => el.classList.toggle("hidden", mode === "leveled"))
    pane.querySelectorAll("[data-lev-only]").forEach(el => el.classList.toggle("hidden", mode !== "leveled"))
  }

  toggleQuestions(e) {
    const pane = e.currentTarget.closest("[data-precon-target='packagePanel']")
    pane.querySelector("[data-precon-s456-target='levqs']")?.classList.toggle("hidden")
  }

  openQuestions() {
    if (!this.p) return
    this.p.showStep(3, true)
    const tab = this.p.element.querySelector("[data-precon-target='panel'][data-step='3'] [data-tab='qa']")
    if (tab) tab.click()
  }

  openCallList() {
    if (!this.p) return
    this.p.showStep(3, true)
    const open = () => {
      const m = this.p.element.querySelector("[data-precon-target='panel'][data-step='3'] [data-modal='call']")
      if (m) m.classList.remove("hidden")
      return !!m
    }
    if (!open()) setTimeout(open, 400)
    track("Precon Call List Opened", { project_id: this.p.projectIdValue, from: "step 4" })
  }

  dropBid(e) {
    const id = e.currentTarget.dataset.bidderId
    if (!this.p) return
    this.p.showStep(3, true)
    const go = () => {
      const btn = this.p.element.querySelector(`[data-precon-target='panel'][data-step='3'] .t3.trk[data-bidder-id='${id}'] [data-action*='openDrop']`)
      if (btn) btn.click()
      return !!btn
    }
    if (!go()) setTimeout(go, 400)
  }

  trackFile() { track("Precon Bid File Opened", { project_id: this.p?.projectIdValue }) }

  _codeLabel(pkgId) {
    const pane = this._pane(pkgId) || this.element.closest("[data-precon-target='packagePanel']")
    const codes = JSON.parse(pane?.dataset.codes || "[]")
    if (codes.length === 1) return `${codes[0].code} ${codes[0].name}`
    return pane?.querySelector(".ptitle")?.textContent?.trim() || ""
  }

  // Carry: a bid as sent or leveled, or our own bid. A bid you added for a
  // sub that they haven't confirmed asks first (Carry without their OK?).
  carry(e) {
    const d = e.currentTarget.dataset
    if (d.noconf === "1") {
      const m = this._modal("nook")
      if (m) {
        m.querySelector("[data-nook='title']").textContent = this._t("nook_title", { sub: d.name })
        m.querySelector("[data-nook='text']").textContent = this._t(d.how === "sent" ? "nook_text_sent" : "nook_text_leveled", { sub: d.name, amount: d.amount })
        this._pendingCarry = { ...d, btn: e.currentTarget }
        m.classList.remove("hidden")
        return
      }
    }
    this._doCarry({ ...d, btn: e.currentTarget })
  }

  nookYes(e) {
    this.closeModal(e)
    if (this._pendingCarry) this._doCarry(this._pendingCarry)
    this._pendingCarry = null
  }

  _doCarry(d) {
    const pkg = d.packageId
    const body = d.rowKey === "self" ? { self_bid: "1" } : { bid_id: d.rowKey, how: d.how }
    if (this.p) this.p._selectedPackage = pkg
    const code = this._codeLabel(pkg)
    // Optimistic: the bid shows carried, the list row shows the number, the
    // carry box says who — right away; the server's answer replaces the pane.
    const restore = this._optimistic(pkg, { btn: d.btn, label: this._t(d.how === "sent" ? "carried_as_sent" : "carried_leveled"),
      box: `${d.name || ""} ${this._t(d.how === "sent" ? "as_sent_word" : "leveled_word")} · ${d.amount || ""}`, amount: d.amount })
    this._send("POST", `packages/${pkg}/carry`, body, {
      keep: pkg,
      undo: restore,
      ok: () => this._t(d.how === "sent" ? "carried_bid_sent" : "carried_bid_leveled", { sub: d.name || "", code, amount: d.amount || "" })
    })
  }

  // Paint a carry (or its undo, with amount null) at once; returns how to put it back.
  _optimistic(pkg, { btn, label, box, amount }) {
    const pane = this._pane(pkg)
    const snapPane = pane ? pane.innerHTML : null
    const rows = Array.from(this.element.querySelectorAll(`[data-precon-target='packageRow'][data-package-id='${pkg}'] .lstat`))
    const snapRows = rows.map(r => [r, r.className, r.innerHTML])
    if (btn) {
      if (amount !== null) {
        const badge = document.createElement("span")
        badge.className = "badge b-solid"
        badge.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4.5 12.75 6 6 9-13.5"/></svg>`
        badge.append(label)
        btn.closest(".sentcard")?.classList.add("ccol")
        btn.replaceWith(badge)
      } else {
        btn.disabled = true
      }
    }
    const head = pane?.querySelector(".phead-r")
    if (head) {
      head.querySelector(".carrybox")?.remove()
      if (amount !== null && box) {
        const cb = document.createElement("div")
        cb.className = "carrybox"
        cb.innerHTML = `<span></span><b></b>`
        cb.querySelector("span").textContent = this._t("carrying")
        cb.querySelector("b").textContent = box
        head.appendChild(cb)
      }
    }
    rows.forEach(r => {
      if (amount !== null) {
        const n = this._num(amount)
        r.className = "lstat ok"
        r.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m4.5 12.75 6 6 9-13.5"/></svg><span>${n >= 1e6 ? "$" + (n / 1e6).toFixed(2) + "M" : "$" + (n / 1e3).toFixed(1) + "k"}</span>`
      } else {
        r.className = "lstat todo"
        r.innerHTML = `<span>${this._esc(this._t("pick"))}</span>`
      }
    })
    return () => {
      if (pane && snapPane !== null) pane.innerHTML = snapPane
      snapRows.forEach(([r, cls, html]) => { r.className = cls; r.innerHTML = html })
    }
  }

  uncarry(e) {
    const pkg = e.currentTarget.dataset.packageId
    const kind = e.currentTarget.dataset.kind
    if (this.p) this.p._selectedPackage = pkg
    const code = this._codeLabel(pkg)
    const restore = this._optimistic(pkg, { btn: e.currentTarget, amount: null })
    this._send("POST", `packages/${pkg}/uncarry`, {}, { undo: restore, keep: pkg, ok: () => this._t(kind === "allowance" ? "ao_removed" : "uncarried", { code }) })
  }

  carryAllowance(e) {
    const row = e.currentTarget.closest(".ownrow")
    const input = row.querySelector("[data-field='ao']")
    const amount = this._num(input.value || "")
    if (!amount) { this._toast(this._t("type_allowance"), "error"); input.focus(); return }
    const pkg = e.currentTarget.dataset.packageId
    if (this.p) this.p._selectedPackage = pkg
    const code = this._codeLabel(pkg)
    e.currentTarget.disabled = true
    const btn = e.currentTarget
    const restore = this._optimistic(pkg, { box: this._t("carry_ao", { amount: this._money(amount) }), amount: String(amount) })
    this._send("POST", `packages/${pkg}/carry`, { allowance_only: "1", amount }, { undo: () => { restore(); btn.disabled = false }, keep: pkg, ok: () => this._t("carried_ao", { amount: this._money(amount), code }) })
  }

  carryOwn(e) {
    const row = e.currentTarget.closest(".ownrow")
    const input = row.querySelector("[data-field='own']")
    const amount = this._num(input.value || "")
    if (!amount) { this._toast(this._t("type_budget"), "error"); input.focus(); return }
    const gc = row.querySelector("[data-field='gc_allowance']")?.checked
    const pkg = e.currentTarget.dataset.packageId
    if (this.p) this.p._selectedPackage = pkg
    const code = this._codeLabel(pkg)
    const company = row.querySelector(".muted")?.dataset.company || ""
    e.currentTarget.disabled = true
    const btn = e.currentTarget
    const restore = this._optimistic(pkg, { box: this._t("carry_budget", { company, amount: this._money(amount) }), amount: String(amount) })
    this._send("POST", `packages/${pkg}/carry`, { self: "1", amount, gc_allowance: gc ? "1" : "0" }, {
      keep: pkg,
      undo: () => { restore(); btn.disabled = false },
      ok: () => this._t(gc ? "carried_gc_allowance" : "carried_budget", { amount: this._money(amount), code, company })
    })
  }

  // Plugs: a gap cell's checkbox and amount, and the AI bid check's Keep / Not needed — one request per bid.
  _plug(cell, on, amount) {
    const d = cell.dataset
    if (this.p) this.p._selectedPackage = d.packageId
    return this._send("POST", `packages/${d.packageId}/plugs`, { bid_id: d.bidId, plugs: [{ cost_code_id: d.costCodeId, line_index: d.lineIndex, name: d.name, on: on ? "1" : "0", amount }] },
      { keep: d.packageId, ok: on ? this._t("plug_on") : this._t("plug_off") })
  }

  togglePlug(e) {
    const cell = e.currentTarget.closest(".gapc")
    const input = cell.querySelector(".plugin")
    const on = e.currentTarget.checked
    if (input) input.disabled = !on
    track("Precon Plug Switched", { project_id: this.p?.projectIdValue, on })
    this._plug(cell, on, input?.value).then(r => { if (!r) { e.currentTarget.checked = !on; if (input) input.disabled = on } })
  }

  plugAmount(e) {
    const cell = e.currentTarget.closest(".gapc")
    this._plug(cell, true, e.currentTarget.value)
  }

  setPlug(e) {
    const d = e.currentTarget.dataset
    e.currentTarget.disabled = true
    if (this.p) this.p._selectedPackage = d.packageId
    this._send("POST", `packages/${d.packageId}/plugs`, { bid_id: d.bidId, plugs: [{ cost_code_id: d.costCodeId, line_index: d.lineIndex, name: d.name, on: d.on, amount: d.amount }] },
      { keep: d.packageId, undo: () => { e.currentTarget.disabled = false }, ok: d.on === "1" ? this._t("plug_on") : this._t("plug_off") })
  }

  toggleAlternate(e) {
    const d = e.currentTarget.dataset
    const accepted = e.currentTarget.type === "checkbox" ? e.currentTarget.checked : d.accepted === "1"
    if (this.p) this.p._selectedPackage = d.packageId
    e.currentTarget.closest(".lc")?.classList.toggle("exoff", !accepted)
    this._send("PATCH", `packages/${d.packageId}/adjustments/${d.adjustmentId}`, { accepted: accepted ? "1" : "0" },
      { keep: d.packageId, ok: accepted ? this._t("alt_counted") : this._t("alt_left_out") })
  }

  toggleAddOn(e) {
    const cell = e.currentTarget.closest(".gapc")
    const d = cell.dataset
    const input = cell.querySelector(".plugin")
    const on = e.currentTarget.checked
    if (input) input.disabled = !on
    if (this.p) this.p._selectedPackage = d.packageId
    if (on) this._send("POST", `packages/${d.packageId}/adjustments`, { kind: "plug", name: d.name, amount: input?.value, bid_id: d.bidId }, { keep: d.packageId, ok: this._t("addon_added") })
    else if (d.adjustmentId) this._send("DELETE", `packages/${d.packageId}/adjustments/${d.adjustmentId}`, undefined, { keep: d.packageId, ok: this._t("addon_removed") })
  }

  addOnAmount(e) {
    const cell = e.currentTarget.closest(".gapc")
    const d = cell.dataset
    if (d.adjustmentId) this._send("PATCH", `packages/${d.packageId}/adjustments/${d.adjustmentId}`, { amount: e.currentTarget.value }, { keep: d.packageId, ok: this._t("plug_saved") })
  }

  removeAdjustment(e) {
    const d = e.currentTarget.dataset
    e.currentTarget.disabled = true
    if (this.p) this.p._selectedPackage = d.packageId
    this._send("DELETE", `packages/${d.packageId}/adjustments/${d.adjustmentId}`, undefined, { keep: d.packageId, undo: () => { e.currentTarget.disabled = false }, ok: this._t("addon_removed") })
  }

  // "Ask Peak" on a line they left out, or "Ask them to confirm": an email with the link to their bid page.
  askSub(e) {
    const d = e.currentTarget.dataset
    const name = e.currentTarget.closest(".sentcard")?.querySelector(".sch b")?.textContent ||
      e.currentTarget.textContent.replace(/^\S+\s/, "")
    const confirm = d.kind === "confirm"
    const sub = confirm ? name : (d.sub || name)
    this._ask(this._t(confirm ? "ask_confirm_title" : "ask_line_title", { sub }), this._t("ask_line_msg", { sub }),
      confirm ? this._t("ask_confirm_default") : this._t("ask_line_default", { item: (d.item || "").replace(/^./, c => c.toLowerCase()) }),
      this._t("send"), (message) => {
        if (this.p) this.p._selectedPackage = d.packageId
        this._send("POST", `packages/${d.packageId}/request_revision`, { bidder_id: d.bidderId, message, kind: confirm ? "confirm" : "line" }, { keep: d.packageId, ok: this._t("asked", { sub }) })
      })
  }

  remind(e) {
    const btn = e.currentTarget
    const id = btn.dataset.bidderId
    const done = document.createElement("span")
    done.className = "muted"
    done.textContent = this._t("reminded_word")
    btn.replaceWith(done)
    this._send("POST", `invites/${id}/remind`, {}, { undo: () => done.replaceWith(btn), ok: this._t("reminded") }).then(r => { if (r) this.p?._markStale([4]) })
  }

  // ── Step 4: Bids placed ───────────────────────────────────────────────
  openBids(e) {
    e.stopPropagation()
    const id = e.currentTarget.dataset.packageId
    const m = this._modal("bc")
    if (!m) return
    m.dataset.view = id
    m.querySelectorAll("[data-bc-view]").forEach(v => v.classList.toggle("hidden", v.dataset.bcView !== id))
    const view = m.querySelector(`[data-bc-view='${id}']`)
    if (!view) return
    m.querySelector("[data-bc='title']").textContent = view.dataset.title
    m.querySelector("[data-bc='sub']").textContent = view.dataset.sub
    m.querySelector("[data-bc='tabs']").classList.toggle("hidden", id !== "all")
    const inv = m.querySelector("[data-bc='invite']")
    inv.classList.toggle("hidden", id === "all")
    inv.dataset.packageId = id
    if (id === "all") this._bcTab(m, m.dataset.tab || "waiting")
    this._bcRemindLabel(m)
    m.classList.remove("hidden")
    track("Precon Bids Placed Opened", { project_id: this.p?.projectIdValue, scope: id === "all" ? "all" : "package" })
  }

  bcTab(e) { this._bcTab(e.currentTarget.closest(".overlay"), e.currentTarget.dataset.tab) }

  _bcTab(m, tab) {
    m.dataset.tab = tab
    m.querySelectorAll("[data-bc='tabs'] .fchip").forEach(c => c.classList.toggle("on", c.dataset.tab === tab))
    const view = m.querySelector("[data-bc-view='all']")
    let shown = 0
    view.querySelectorAll("[data-bc-block]").forEach(b => {
      b.querySelectorAll(".bcrow").forEach(r => r.classList.toggle("hidden", tab === "waiting" && r.dataset.kind === "in"))
      const show = tab === "all" || b.dataset.hasWaiting === "1"
      b.classList.toggle("hidden", !show)
      if (show) shown++
    })
    const empty = view.querySelector("[data-bc='emptyAll']")
    empty.classList.toggle("hidden", shown > 0)
    empty.textContent = view.querySelectorAll(".bcrow").length ? empty.dataset.done : empty.dataset.none
    this._bcRemindLabel(m)
  }

  _bcRemindLabel(m) {
    const view = m.querySelector(`[data-bc-view='${m.dataset.view}']`)
    const ids = view ? Array.from(view.querySelectorAll(".bcrow[data-remind='1']")).map(r => r.dataset.bidderId) : []
    const anyWaiting = view ? view.querySelectorAll(".bcrow[data-kind='waiting']").length : 0
    const btn = m.querySelector("[data-bc='remindAll']")
    btn.classList.toggle("hidden", !anyWaiting)
    btn.disabled = ids.length === 0
    btn.dataset.ids = [...new Set(ids)].join(",")
    m.querySelector("[data-bc='remindLabel']").textContent = ids.length ? this._t("remind_all", { count: new Set(ids).size }) : this._t("remind_all_done")
  }

  bcOpenPackage(e) {
    const id = e.currentTarget.dataset.packageId
    this.closeModal(e)
    this._select(id, true)
  }

  bcInvite(e) {
    const id = e.currentTarget.dataset.packageId
    this.closeModal(e)
    const code = this._codeLabel(id)
    this.p?.goToPackage({ currentTarget: { dataset: { step: "2", packageId: id } } })
    this._toast(this._t("invite_more_toast", { code }))
  }

  bcRemindAll(e) {
    const m = e.currentTarget.closest(".overlay")
    const ids = (e.currentTarget.dataset.ids || "").split(",").filter(Boolean)
    if (!ids.length) return
    e.currentTarget.disabled = true
    this._send("POST", "invites/remind", { ids, message: m.dataset.autoReminder, via: "email" }, {
      undo: () => { e.currentTarget.disabled = false },
      ok: this._t("reminded_many")
    }).then(r => {
      if (!r) return
      m.querySelectorAll(".bcrow[data-remind='1']").forEach(row => {
        if (!ids.includes(row.dataset.bidderId)) return
        row.dataset.remind = "0"
        const b = row.querySelector(".r button")
        if (b) { const s = document.createElement("span"); s.className = "muted"; s.textContent = this._t("reminded_word"); b.replaceWith(s) }
      })
      this._bcRemindLabel(m)
      this.p?._markStale([4])
    })
  }

  // ── Step 4: their bid, as they sent it ────────────────────────────────
  openBreakdown(e) {
    const id = e.currentTarget.dataset.bidId
    const m = this._modal("bd")
    const tpl = (this.element.closest("[data-precon-target='panel']") || this.element).querySelector(`template[data-bd-for='${id}']`)
    if (!m || !tpl) return
    m.querySelector("[data-bd='title']").textContent = tpl.dataset.title
    const body = m.querySelector("[data-bd='body']")
    body.innerHTML = ""
    body.appendChild(tpl.content.cloneNode(true))
    m.classList.remove("hidden")
    track("Precon Bid Breakdown Opened", { project_id: this.p?.projectIdValue })
  }

  // ── Step 4: our own bid (the same lines as step 2) ────────────────────
  openSelfBid(e) {
    const id = e.currentTarget.dataset.packageId
    const pane = this._pane(id) || e.currentTarget.closest("[data-precon-target='packagePanel']")
    const m = this._modal("self")
    if (!m || !pane) return
    this._se = { pkg: id, codes: JSON.parse(pane.dataset.codes || "[]"), low: pane.dataset.lowSub ? JSON.parse(pane.dataset.lowSub) : null,
                 lines: JSON.parse(pane.dataset.selfLines || "[]").map(l => ({ ...l })) }
    if (!this._se.lines.length) this._se.lines.push({ d: "", q: "", u: this._t("self_unit_ls"), c: "", code: this._se.codes[0]?.code || "" })
    m.querySelector("[data-se='title']").textContent = pane.dataset.selfTitle
    this._seRender(m)
    m.classList.remove("hidden")
  }

  _seRender(m) {
    const rows = m.querySelector("[data-se='rows']")
    const codes = this._se.codes
    rows.innerHTML = this._se.lines.map((l, k) => {
      const tot = this._num(l.q) * this._num(l.c)
      const code = codes.find(c => c.code === l.code) || codes[0]
      return `<div class="set" data-k="${k}">
        <input class="inp" type="text" value="${this._esc(l.d)}" data-f="d" aria-label="${this._esc(this._t("se_what"))}">
        <input class="inp r" type="text" value="${this._esc(l.q)}" data-f="q" aria-label="${this._esc(this._t("se_qty"))}">
        <span class="muted">${this._esc(l.u || "")}</span>
        <input class="inp r" type="text" value="${this._esc(l.c)}" data-f="c" aria-label="${this._esc(this._t("se_unit_cost"))}">
        <span class="r strong" data-tot>${this._money(tot)}</span>
        <button class="setx" type="button" data-remove aria-label="${this._esc(this._t("se_remove"))}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"/></svg></button>
        <span class="setmeta">${code ? this._esc(this._t("self_goes_in", { code: `${code.code} ${code.name}` })) : ""}</span></div>`
    }).join("")
    rows.querySelectorAll("input").forEach(i => i.addEventListener("input", () => {
      const k = parseInt(i.closest(".set").dataset.k, 10)
      this._se.lines[k][i.dataset.f] = i.value
      i.closest(".set").querySelector("[data-tot]").textContent = this._money(this._num(this._se.lines[k].q) * this._num(this._se.lines[k].c))
      this._seTotal(m)
    }))
    rows.querySelectorAll("[data-remove]").forEach(b => b.addEventListener("click", () => {
      this._se.lines.splice(parseInt(b.closest(".set").dataset.k, 10), 1)
      this._seRender(m)
    }))
    this._seTotal(m)
  }

  _seTotal(m) {
    const total = this._se.lines.reduce((a, l) => a + this._num(l.q) * this._num(l.c), 0)
    m.querySelector("[data-se='total']").textContent = this._money(total)
    const vs = m.querySelector("[data-se='vs']")
    const low = this._se.low
    if (low && total) {
      const below = total <= low.leveled
      vs.className = below ? "under" : "over"
      vs.textContent = this._t(below ? "self_vs_below" : "self_vs_above", { amount: this._money(Math.abs(low.leveled - total)), sub: low.name })
    } else { vs.textContent = ""; vs.className = "" }
  }

  seAdd(e) {
    const m = e.currentTarget.closest(".overlay")
    this._se.lines.push({ d: "", q: "", u: this._t("self_unit_ls"), c: "", code: this._se.codes[0]?.code || "" })
    this._seRender(m)
    m.querySelector(".set:last-child input")?.focus()
  }

  async seDone(e) {
    const m = e.currentTarget.closest(".overlay")
    const lines = this._se.lines.filter(l => String(l.d).trim() || this._num(l.q) || this._num(l.c))
    m.classList.add("hidden")
    try {
      const data = await this.p._json("PATCH", `${this.base}/bidders/our_bid`, { package_id: this._se.pkg, lines })
      this.p._markStale([2, 4, 5, 6])
      if (data?.summary) this.p._applySummary(data.summary)
      this._toast(this._t("self_saved"))
      track("Precon Own Bid Saved", { project_id: this.p.projectIdValue, from: "step 4", lines: lines.length })
    } catch (err) {
      this._toast(err.message, "error")
    }
  }

  // "+ Add a line item": your own line in that division of the main budget.
  addLineItem(e) {
    const div = e.currentTarget.dataset.division || ""
    const label = e.currentTarget.dataset.divisionLabel || div
    this._ask(this._t("add_line_title"), this._t("add_line_msg", { division: label }), "", this._t("add"), (name, amount) => {
      this._send("POST", "budget_lines", { name, amount, code: div, kind: "own" }, { ok: this._t("line_added") })
    }, { amount: true })
  }

  // ── Step 5: views, open rows, sort ────────────────────────────────────
  _restore5() {
    const st = this.st
    const view = st.view || this._ls(VIEW_KEY)
    if (view && ["divisions", "lines", "items"].includes(view)) this._view(view)
    else this._view(this.element.querySelector("[data-precon-s456-target='budgetRows']")?.dataset.view || "lines")
    if (st.bv) this._compare(st.bv)
  }

  budgetView(e) {
    const v = e.currentTarget.dataset.view
    this._view(v)
    this.st.view = v
    this._ls(VIEW_KEY, v)
    track("Precon Budget View Switched", { project_id: this.p?.projectIdValue, view: v })
  }

  _view(v) {
    const rows = this.element.querySelector("[data-precon-s456-target='budgetRows']")
    if (!rows) return
    rows.dataset.view = v
    this.element.querySelectorAll("[data-precon-s456-target='viewSeg'] .segbtn").forEach(b => b.classList.toggle("on", b.dataset.view === v))
    Array.from(rows.children).forEach(el => {
      if (el.classList.contains("bdiv")) return
      if (v === "divisions") { el.classList.add("hidden"); return }
      if (el.dataset.itemOf) {
        const open = v === "items" || !!this.st.open[el.dataset.itemOf]
        el.classList.toggle("hidden", !open)
        return
      }
      el.classList.remove("hidden")
    })
    rows.querySelectorAll("[data-code-row] .chev").forEach(c => c.classList.toggle("open", v === "items" || !!this.st.open[c.closest("[data-code-row]").dataset.codeRow]))
  }

  toggleBudgetRow(e) {
    if (e.target.closest(".ncell")) return
    const row = e.currentTarget
    const id = row.dataset.codeRow
    const rows = this.element.querySelector("[data-precon-s456-target='budgetRows']")
    if (rows?.dataset.view === "items") return
    this.st.open[id] = !this.st.open[id]
    this._view(rows.dataset.view)
  }

  sortBudget(e) {
    const build = e.currentTarget.dataset.sort === "build"
    this.element.querySelectorAll(".sortseg .segbtn").forEach(b => { const on = b === e.currentTarget; b.classList.toggle("on", on); b.setAttribute("aria-pressed", on) })
    track("Precon Budget Sorted", { project_id: this.p?.projectIdValue, sort: build ? "build" : "csi" })
    this._send("PATCH", "cost_codes/settings", { sort_build: build ? "1" : "0" }, { quiet: true }).then(r => { if (r) this.p?._markStale([1, 2, 3, 4, 5, 6]) })
  }

  editOrder() {
    if (!this.p) return
    this.p.showStep(1, true)
    const go = () => {
      const b = this.p.element.querySelector("[data-precon-target='panel'][data-step='1'] [data-action*='openOrder']")
      if (b) b.click()
      return !!b
    }
    if (!go()) setTimeout(go, 400)
  }

  // ── Step 5: internal notes and their files ────────────────────────────
  addNote(e) {
    e.stopPropagation()
    const cell = e.currentTarget.closest(".ncell")
    const input = cell.querySelector(".ninp")
    e.currentTarget.classList.add("hidden")
    input.classList.remove("hidden")
    input.focus()
  }

  noteBlur(e) {
    const cell = e.currentTarget.closest(".ncell")
    if (!e.currentTarget.value.trim()) {
      e.currentTarget.classList.add("hidden")
      cell.querySelector(".addnote")?.classList.remove("hidden")
    }
  }

  async saveNote(e) {
    const cell = e.currentTarget.closest(".ncell")
    const body = e.currentTarget.value.trim()
    try {
      await this.p._json("PATCH", `${this.base}/budget_notes`, { key: cell.dataset.noteKey, body })
      this._toast(this._t(body ? "note_saved" : "note_removed"))
      track("Precon Budget Note Saved", { project_id: this.p.projectIdValue, cleared: !body })
    } catch (err) { this._toast(err.message, "error") }
  }

  openFiles(e) {
    e.stopPropagation()
    const cell = e.currentTarget.closest(".ncell")
    const m = this._modal("files")
    if (!m) return
    this._filesCell = cell
    m.querySelector("[data-files='sub']").textContent = cell.dataset.label
    const note = cell.querySelector(".ninp")?.value?.trim()
    const q = m.querySelector("[data-files='note']")
    q.textContent = note || ""
    q.classList.toggle("hidden", !note)
    this._renderFileChips()
    m.classList.remove("hidden")
  }

  _fsize(n) { if (!n) return ""; return n >= 1048576 ? (n / 1048576).toFixed(1) + " MB" : Math.max(1, Math.round(n / 1024)) + " KB" }

  _renderFileChips() {
    const m = this._modal("files")
    const files = JSON.parse(this._filesCell?.dataset.files || "[]")
    const box = m.querySelector("[data-files='chips']")
    box.querySelectorAll(".fchipn:not(.up)").forEach(c => c.remove())
    files.forEach(f => {
      const chip = document.createElement("span")
      chip.className = "fchipn"
      chip.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z"/></svg><a class="trunc" target="_blank" rel="noopener"></a><span class="muted"></span><button class="fx" type="button"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"/></svg></button>`
      const a = chip.querySelector("a"); a.textContent = f.name; a.href = f.url
      chip.querySelector(".muted").textContent = this._fsize(f.size)
      const x = chip.querySelector(".fx")
      x.setAttribute("aria-label", this._t("remove_file", { name: f.name }))
      x.addEventListener("click", () => this._removeNoteFile(f.id, chip))
      box.appendChild(chip)
    })
    m.querySelector("[data-files='none']").classList.toggle("hidden", files.length > 0 || box.querySelector(".fchipn"))
  }

  async _removeNoteFile(id, chip) {
    chip.remove()
    const files = JSON.parse(this._filesCell.dataset.files || "[]").filter(f => f.id !== id)
    this._filesCell.dataset.files = JSON.stringify(files)
    this._paintClip()
    this._renderFileChips()
    try { await this.p._json("DELETE", `${this.base}/budget_files/${id}`) } catch (err) { this._toast(err.message, "error") }
  }

  _paintClip() {
    const cell = this._filesCell
    if (!cell) return
    const n = JSON.parse(cell.dataset.files || "[]").length
    const clip = cell.querySelector(".clip")
    clip.classList.toggle("has", n > 0)
    let span = clip.querySelector("span")
    if (n && !span) { span = document.createElement("span"); clip.appendChild(span) }
    if (span) { if (n) span.textContent = n; else span.remove() }
  }

  dzOver(e) { e.preventDefault(); e.currentTarget.classList.add("over") }
  dzLeave(e) { e.currentTarget.classList.remove("over") }
  dzDrop(e) { e.preventDefault(); e.currentTarget.classList.remove("over"); this._uploadNoteFiles(Array.from(e.dataTransfer?.files || [])) }
  pickNoteFiles(e) { this._uploadNoteFiles(Array.from(e.currentTarget.files || [])); e.currentTarget.value = "" }

  // presign → S3 PUT → register, with the chip pattern (blue + spinner, then gray with ×).
  async _uploadNoteFiles(files, purpose = "note") {
    if (!files.length || !this.p) return
    const m = this._modal(purpose === "note" ? "files" : "pkgfiles") || this._modal("files")
    const box = purpose === "note" ? m.querySelector("[data-files='chips']") : this.element.querySelector("[data-s6='fileChips']")
    const chips = files.map(f => {
      const c = document.createElement("span")
      c.className = "fchipn up"
      c.innerHTML = `<span class="spin2"></span><span class="trunc"></span><span class="muted"></span>`
      c.querySelector(".trunc").textContent = f.name
      c.querySelector(".muted").textContent = this._fsize(f.size)
      box?.appendChild(c)
      return c
    })
    if (purpose === "note") m.querySelector("[data-files='none']").classList.add("hidden")
    try {
      const presign = await this.p._json("POST", this.p.presignUrlValue, { files: files.map(f => ({ filename: f.name, content_type: f.type || "application/octet-stream", byte_size: f.size })) })
      await Promise.all(presign.uploads.map((u, i) => fetch(u.upload_url, { method: "PUT", body: files[i], headers: { "Content-Type": u.content_type || files[i].type || "application/octet-stream" } })
        .then(r => { if (!r.ok) throw new Error(this._t("upload_failed", { name: files[i].name })) })))
      const data = await this.p._json("POST", `${this.base}/budget_files`, {
        purpose, note_key: purpose === "note" ? this._filesCell?.dataset.noteKey : undefined,
        files: presign.uploads.map((u, i) => ({ cache_name: u.cache_name, filename: files[i].name, content_type: files[i].type, byte_size: files[i].size }))
      })
      chips.forEach(c => c.remove())
      if (purpose === "note") {
        const list = JSON.parse(this._filesCell.dataset.files || "[]").concat(data.files || [])
        this._filesCell.dataset.files = JSON.stringify(list)
        this._paintClip()
        this._renderFileChips()
      } else {
        this._addPackageFiles(data.files || [])
      }
      this._toast(files.length === 1 ? this._t("file_added") : this._t("files_added", { count: files.length }))
      track(purpose === "note" ? "Precon Note Files Added" : "Precon Package File Added", { project_id: this.p.projectIdValue, count: files.length })
    } catch (err) {
      chips.forEach(c => c.remove())
      this._toast(err.message, "error")
    }
  }

  // ── Step 5: lines, markups, the general conditions calculator ─────────
  changeMarkup(e) {
    const key = e.currentTarget.dataset.key
    const mk = JSON.parse(this.element.dataset.markups || "[]")
    const was = JSON.stringify(mk)
    mk.forEach(m => { if (m[0] === key) m[1] = this._num(e.currentTarget.value) })
    this.element.dataset.markups = JSON.stringify(mk)
    this._recalc5()
    track("Precon Markup Changed", { project_id: this.p?.projectIdValue, markup: key })
    this._send("PATCH", "markups", { [key]: e.currentTarget.value }, { ok: this._t("markup_saved"), undo: () => { this.element.dataset.markups = was; this._recalc5() } })
  }

  // Optimistic: markups compound on the cost (contingency, insurance, bond,
  // fee — each on everything above it); the totals, tiles and shares follow.
  _recalc5(directDelta = 0) {
    const root = this.element
    const direct = parseFloat(root.dataset.direct || "0") + directDelta
    root.dataset.direct = String(direct)
    const trade = parseFloat(root.dataset.trade || "0")
    const sf = parseFloat(root.dataset.sf || "0")
    let running = direct
    const amounts = {}
    JSON.parse(root.dataset.markups || "[]").forEach(([k, p]) => { const a = Math.round(running * p / 100); amounts[k] = a; running += a })
    const total = running
    Object.entries(amounts).forEach(([k, a]) => {
      const cell = root.querySelector(`[data-mk-amt='${k}']`)
      if (cell) cell.textContent = Math.round(a).toLocaleString("en-US")
      const sh = root.querySelector(`[data-mk-share='${k}']`)
      if (sh) sh.dataset.share = String(a)
    })
    root.querySelectorAll("[data-share]").forEach(sh => { sh.textContent = total ? `${(parseFloat(sh.dataset.share) * 100 / total).toFixed(1)}%` : "" })
    const set = (k, v) => { const el = root.querySelector(`[data-s5='${k}']`); if (el) el.textContent = v }
    set("total", this._money(total))
    set("grand", Math.round(total).toLocaleString("en-US"))
    set("yours", this._money(total - trade))
    if (sf > 0) { set("psf", this._money(total / sf)); set("psfLabel", this._t("per_sf_label", { amount: this._money(total / sf) })) }
  }

  removeLine(e) {
    const id = e.currentTarget.dataset.lineId
    const row = e.currentTarget.closest(".t5")
    const amt = this._num(row?.querySelector("[data-share]")?.dataset.share)
    this.p?._confirm(this._t("remove_line_title"), this._t("remove_line_text"), this._t("remove"), () => {
      row?.classList.add("hidden")
      row?.querySelector("[data-share]")?.setAttribute("data-share", "0")
      this._recalc5(-amt)
      this._send("DELETE", `budget_lines/${id}`, undefined, { undo: () => { row?.classList.remove("hidden"); row?.querySelector("[data-share]")?.setAttribute("data-share", String(amt)); this._recalc5(amt) }, ok: this._t("line_removed") })
    })
  }

  openOwnForm(e) {
    const wrap = e.currentTarget.closest(".t5add")
    e.currentTarget.classList.add("hidden")
    const f = wrap.querySelector(".addform")
    f.classList.remove("hidden")
    f.querySelector("[data-field='name']").focus()
  }

  cancelOwnLine(e) {
    const wrap = e.currentTarget.closest(".t5add")
    wrap.querySelector(".addform").classList.add("hidden")
    wrap.querySelector(".linkbtn").classList.remove("hidden")
  }

  saveOwnLine(e) {
    e.preventDefault()
    const f = e.currentTarget.closest(".addform")
    const name = f.querySelector("[data-field='name']").value.trim()
    const amount = f.querySelector("[data-field='amount']").value
    if (!name) { f.querySelector("[data-field='name']").focus(); return }
    if (!this._num(amount)) { f.querySelector("[data-field='amount']").focus(); return }
    this._send("POST", "budget_lines", { name, amount, kind: "own" }, { ok: this._t("line_added") })
  }

  openGcc() {
    const m = this._modal("gcc")
    if (!m) return
    this._gcc = { len: m.dataset.len, rows: JSON.parse(m.dataset.rows || "[]").map((r, i) => ({ ...r, id: `r${i}` })), codes: JSON.parse(m.dataset.codes || "[]") }
    m.querySelector("[data-gcc='len']").value = this._gcc.len
    this._gccRender(m)
    m.classList.remove("hidden")
    track("Precon General Conditions Opened", { project_id: this.p?.projectIdValue })
  }

  _gccRowTotal(r, len) { const per = this._num(r.per); return r.once ? per : per * (r.months ? this._num(r.months) : len) }

  _gccRender(m) {
    const len = Math.max(1, Math.round(this._num(this._gcc.len)) || 1)
    let total = 0
    const box = m.querySelector("[data-gcc='groups']")
    const xsvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 18 18 6M6 6l12 12"/></svg>`
    box.innerHTML = this._gcc.codes.map(g => {
      const rs = this._gcc.rows.filter(r => r.code === g.code)
      const sum = rs.length ? rs.reduce((a, r) => a + this._gccRowTotal(r, len), 0) : g.est
      total += sum
      const label = `${g.code} ${g.name}`
      return `<div class="gccg"><span>${this._esc(label)}</span><span>${this._money(sum)}</span></div>` +
        (rs.length ? "" : `<div class="gccadd"><span class="muted">${this._esc(this._t("gcc_no_rows", { amount: this._money(g.est) }))}</span></div>`) +
        rs.map(r => `<div class="gccr" data-id="${r.id}">
          <input class="inp" type="text" value="${this._esc(r.name)}" placeholder="${this._esc(this._t("gcc_item_ph"))}" aria-label="${this._esc(this._t("gcc_item"))}" data-f="name">
          <span class="gccm"><span>$</span><input class="inp r" type="text" value="${this._esc(r.per === "" || r.per == null ? "" : Math.round(this._num(r.per)).toLocaleString("en-US"))}" aria-label="${this._esc(r.once ? this._t("gcc_cost") : this._t("gcc_per_month"))}" data-f="per"></span>
          <span class="r">${r.once ? `<span class="muted">${this._esc(this._t("gcc_one_time"))}</span>` : `<input class="inp r" type="text" value="${this._esc(r.months ? r.months : String(len))}" aria-label="${this._esc(this._t("gcc_months_for", { name: r.name || "" }))}" title="${this._esc(r.months ? this._t("gcc_own_months") : this._t("gcc_follows"))}" data-f="months">`}</span>
          <span class="r strong">${this._money(this._gccRowTotal(r, len))}</span>
          <button class="x" type="button" aria-label="${this._esc(this._t("gcc_remove", { name: r.name || "" }))}" data-remove>${xsvg}</button></div>`).join("") +
        `<div class="gccadd"><button class="linkbtn" type="button" data-add="${this._esc(g.code)}">${this._esc(this._t("gcc_add_row", { label }))}</button></div>`
    }).join("")
    m.querySelector("[data-gcc='total']").textContent = this._money(total)
    m.querySelector("[data-gcc='lenHint']").textContent = this._t("gcc_len_hint", { months: len })
    box.querySelectorAll(".gccr input").forEach(i => i.addEventListener("change", () => {
      const r = this._gcc.rows.find(x => x.id === i.closest(".gccr").dataset.id)
      if (i.dataset.f === "months") { const v = i.value.trim(); r.months = !v || Math.round(this._num(v)) === len ? null : v }
      else r[i.dataset.f] = i.value
      this._gccRender(m)
    }))
    box.querySelectorAll("[data-remove]").forEach(b => b.addEventListener("click", () => { this._gcc.rows = this._gcc.rows.filter(x => x.id !== b.closest(".gccr").dataset.id); this._gccRender(m) }))
    box.querySelectorAll("[data-add]").forEach(b => b.addEventListener("click", () => {
      this._gcc.rows.push({ id: `n${Date.now()}`, code: b.dataset.add, name: "", per: "" })
      this._gccRender(m)
      box.querySelector(`.gccr[data-id='${this._gcc.rows[this._gcc.rows.length - 1].id}'] input`)?.focus()
    }))
  }

  gccLen(e) { this._gcc.len = e.currentTarget.value; clearTimeout(this._gccT); this._gccT = setTimeout(() => this._gccRender(e.currentTarget.closest(".overlay")), 300) }

  gccSave(e) {
    const m = e.currentTarget.closest(".overlay")
    m.classList.add("hidden")
    const rows = this._gcc.rows.filter(r => String(r.name).trim() || this._num(r.per)).map(r => ({ code: r.code, name: r.name, per: r.per, months: r.months || "", once: r.once ? "1" : "0" }))
    const total = m.querySelector("[data-gcc='total']").textContent
    this._send("PATCH", "gc_calc", { len: this._gcc.len, rows }, { ok: this._t("gcc_saved", { amount: total }) })
  }

  // ── Step 5: budget versions ───────────────────────────────────────────
  compareVersion(e) {
    const id = e.currentTarget.dataset.versionId
    this._compare(this.st.bv === id ? null : id)
    track("Precon Budget Versions Compared", { project_id: this.p?.projectIdValue })
  }

  _compare(id) {
    this.st.bv = id
    const box = this.element.querySelector("[data-precon-s456-target='versions']")
    if (!box) return
    box.querySelectorAll(".bvpick .fchip").forEach(c => { const on = c.dataset.versionId === id; c.classList.toggle("on", on); c.setAttribute("aria-pressed", on) })
    box.querySelectorAll("[data-version-panel]").forEach(p => p.classList.toggle("hidden", p.dataset.versionPanel !== id))
    const panel = id && box.querySelector(`[data-version-panel='${id}']`)
    if (panel) { const cb = panel.querySelector(".bvhead input[type=checkbox]"); cb.checked = !!this.st.bvSame; this._same(panel, cb.checked) }
  }

  toggleSame(e) { this.st.bvSame = e.currentTarget.checked; this._same(e.currentTarget.closest("[data-version-panel]"), e.currentTarget.checked) }

  _same(panel, on) {
    panel.querySelectorAll(".bvt[data-same='1']").forEach(r => r.classList.toggle("hidden", !on))
    const changed = panel.querySelectorAll(".bvt[data-same='0']:not(.div)").length
    panel.querySelector("[data-no-rows]")?.classList.toggle("hidden", on || changed > 0)
  }

  closeCompare() { this._compare(null) }

  openSaveVersion() {
    const m = this._modal("bvsave")
    if (!m) return
    m.querySelector("[data-bv='name']").value = ""
    m.querySelector("[data-bv='note']").value = ""
    m.querySelector("[data-bv='go']").disabled = true
    m.classList.remove("hidden")
    m.querySelector("[data-bv='name']").focus()
  }

  bvNameInput(e) { e.currentTarget.closest(".modal").querySelector("[data-bv='go']").disabled = !e.currentTarget.value.trim() }

  saveVersion(e) {
    const m = e.currentTarget.closest(".overlay")
    const name = m.querySelector("[data-bv='name']").value.trim()
    if (!name) return
    m.classList.add("hidden")
    this._send("POST", "budget_versions", { name, note: m.querySelector("[data-bv='note']").value }, { ok: this._t("version_saved", { name }) })
  }

  askDeleteVersion(e) {
    const m = this._modal("bvdel")
    if (!m) return
    this._delVersion = { id: e.currentTarget.dataset.versionId, name: e.currentTarget.dataset.name }
    m.querySelector("[data-bv='delTitle']").textContent = this._t("delete_version_title", { name: this._delVersion.name })
    m.classList.remove("hidden")
  }

  deleteVersion(e) {
    e.currentTarget.closest(".overlay").classList.add("hidden")
    const v = this._delVersion
    if (!v) return
    if (this.st.bv === v.id) this.st.bv = null
    this._send("DELETE", `budget_versions/${v.id}`, undefined, { ok: this._t("version_deleted", { name: v.name }) })
  }

  // ── Step 6: what the owner sees (kept as set, the paper follows at once) ──
  get _s6() {
    if (!this._s6state) {
      const st = JSON.parse(this.element.dataset.settings || "{}")
      this._s6state = { detail: st.detail || "division", names: !!st.names, divs_off: (st.divs_off || []).map(String), codes_on: (st.codes_on || []).map(String),
        excl_off: (st.excl_off || []).map(String), excl_own: st.excl_own || [], note_on: st.note_on !== false, note: st.note ?? this.element.querySelector("[data-s6='note']")?.value ?? null, files_off: (st.files_off || []).map(String), old_versions: !!st.old_versions }
    }
    return this._s6state
  }

  _save6() {
    clearTimeout(this._s6timer)
    this._s6pending = new Promise(resolve => {
      this._s6timer = setTimeout(async () => {
        const st = this._s6
        try {
          await this.p._json("PATCH", `${this.base}/owner_settings`, { detail: st.detail, names: st.names ? "1" : "0", divs_off: st.divs_off, codes_on: st.codes_on,
            excl_off: st.excl_off, excl_own: st.excl_own, note_on: st.note_on ? "1" : "0", ...(st.note === null ? {} : { note: st.note }), files_off: st.files_off, old_versions: st.old_versions ? "1" : "0" })
        } catch (err) { this._toast(err.message, "error") }
        resolve()
      }, 350)
    })
    return this._s6pending
  }

  async _flush6() { if (this._s6timer && this._s6pending) await this._s6pending }

  _paint6() {
    const st = this._s6
    const data = JSON.parse(this.element.dataset.paper || "{}")
    const rows = []
    if (data.owner_rows) data.owner_rows.forEach(r => rows.push(r))
    else if (st.detail === "summary") rows.push([data.summary[0], data.summary[1], "pdiv"])
    else {
      let other = 0, otherN = 0
      ;(data.divisions || []).forEach(d => {
        if (st.detail === "division" && st.divs_off.includes(String(d.code))) { other += d.total; otherN++; return }
        rows.push([`${d.code} ${d.name}`, d.total, "pdiv"])
        d.lines.forEach(l => {
          if (st.detail !== "full" && !st.codes_on.includes(String(l.id))) return
          rows.push([`${l.code} ${l.name}${st.names && l.sub ? " — " + l.sub : ""}`, l.amount, "pline"])
        })
      })
      if (otherN) rows.push([this._t(otherN === 1 ? "p_other_one" : "p_other", { count: otherN }), other, "pdiv"])
    }
    if (!data.owner_rows) (data.tail || []).forEach(r => rows.push(r))
    const box = this.element.querySelector("[data-s6='rows']")
    if (box) {
      const grand = box.querySelector(".pgrand")
      box.innerHTML = rows.map(([n, a, c]) => `<div class="pp-row ${c}"><span>${this._esc(n)}</span><span>${this._money(a)}</span></div>`).join("")
      if (grand) box.appendChild(grand)
    }
    const ex = this.element.querySelector("[data-s6='ppEx']")
    if (ex) ex.innerHTML = Array.from(this.element.querySelectorAll("[data-s6='exlist'] input:checked")).map(i => `<li>${this._esc(i.closest(".ex").querySelector(".block").textContent)}</li>`).join("")
    const note = this.element.querySelector("[data-s6='ppNote']")
    const text = this.element.querySelector("[data-s6='note']")?.value || ""
    if (note) { note.textContent = text; note.classList.toggle("hidden", !(st.note_on && text.trim())) }
    const parts = [this._t("part_proposal")].concat(Array.from(this.element.querySelectorAll("[data-s6='files'] input[data-file-id]:checked")).map(i => i.dataset.name))
    const ph = this.element.querySelector("[data-s6='partsHead']")
    if (ph) ph.textContent = this._t(parts.length === 1 ? "parts_head_one" : "parts_head", { count: parts.length })
    const pl = this.element.querySelector("[data-s6='parts']")
    if (pl) pl.textContent = parts.join(" · ")
    const send = this.element.querySelector("[data-s6='sendLabel']")
    if (send && !send.dataset.sent) send.textContent = this._t(parts.length === 1 ? "send_n_one" : "send_n", { count: parts.length })
    this._paintTree()
  }

  _paintTree() {
    const st = this._s6
    const data = JSON.parse(this.element.dataset.paper || "{}")
    let shown = 0, codes = 0
    ;(data.divisions || []).forEach(d => {
      const on = !st.divs_off.includes(String(d.code))
      const row = this.element.querySelector(`.ptd[data-div='${d.code}']`)
      if (!row) return
      row.classList.toggle("off", !on)
      row.querySelector("input").checked = on
      const nOn = d.lines.filter(l => st.codes_on.includes(String(l.id))).length
      const meta = row.querySelector(".ptm")
      meta.textContent = on ? (nOn ? this._t("n_of_codes", { shown: nOn, total: d.lines.length }) : meta.dataset.total) : this._t("in_other_work")
      this.element.querySelectorAll(`[data-div-subs='${d.code}'] .pts`).forEach(lab => {
        const cb = lab.querySelector("input")
        lab.classList.toggle("dim", !on)
        cb.disabled = !on
        cb.checked = on && st.codes_on.includes(String(cb.dataset.codeId))
      })
      if (on) { shown++; codes += nOn }
    })
    const total = (data.divisions || []).length
    const cnt = this.element.querySelector("[data-s6='breakCount']")
    if (cnt) cnt.textContent = this._t(codes === 1 ? "break_count_one" : "break_count", { shown, total, codes })
    this.element.querySelector("[data-s6='breakAll']")?.classList.toggle("hidden", shown >= total)
    this.element.querySelector("[data-s6='breakNoSubs']")?.classList.toggle("hidden", codes === 0)
    this.element.querySelector("[data-s6='otherHint']")?.classList.toggle("hidden", shown >= total)
  }

  saveOwner(e) {
    const field = e.currentTarget.dataset.field
    this._send("PATCH", "owner", { [field]: e.currentTarget.value.trim() }, { quiet: true, ok: this._t("saved") })
  }

  ownerNameInput(e) { const n = this.element.querySelector("[data-s6='ownerName']"); if (n) n.textContent = e.currentTarget.value }

  setDetail(e) {
    const d = e.currentTarget.dataset.detail
    this._s6.detail = d
    e.currentTarget.parentElement.querySelectorAll(".segbtn").forEach(b => b.classList.toggle("on", b === e.currentTarget))
    this.element.querySelector("[data-s6='break']")?.classList.toggle("hidden", d !== "division")
    this._paint6(); this._save6()
    track("Precon Owner Detail Switched", { project_id: this.p?.projectIdValue, detail: d })
  }

  setFlag(e) {
    const k = e.currentTarget.dataset.set
    this._s6[k] = e.currentTarget.checked
    if (k === "note_on") this.element.querySelector("[data-s6='note']")?.classList.toggle("hidden", !e.currentTarget.checked)
    if (k === "old_versions") { const h = this.element.querySelector("[data-s6='verHint']"); if (h) h.textContent = e.currentTarget.checked ? h.dataset.on : h.dataset.off }
    this._paint6(); this._save6()
    track("Precon Owner Package Option Switched", { project_id: this.p?.projectIdValue, option: k, on: e.currentTarget.checked })
  }

  toggleDiv(e) {
    const code = e.currentTarget.closest(".ptd").dataset.div
    const st = this._s6
    st.divs_off = e.currentTarget.checked ? st.divs_off.filter(c => c !== code) : [...new Set([...st.divs_off, code])]
    this._paint6(); this._save6()
  }

  openDiv(e) {
    const row = e.currentTarget.closest(".ptd")
    const subs = this.element.querySelector(`[data-div-subs='${row.dataset.div}']`)
    const open = subs.classList.contains("hidden")
    subs.classList.toggle("hidden", !open)
    e.currentTarget.setAttribute("aria-expanded", open)
    e.currentTarget.querySelector(".chev")?.classList.toggle("open", open)
  }

  toggleCode(e) {
    const id = String(e.currentTarget.dataset.codeId)
    const st = this._s6
    st.codes_on = e.currentTarget.checked ? [...new Set([...st.codes_on, id])] : st.codes_on.filter(c => c !== id)
    this._paint6(); this._save6()
  }

  breakAll() { this._s6.divs_off = []; this._paint6(); this._save6() }
  breakNoSubs() { this._s6.codes_on = []; this._paint6(); this._save6() }

  toggleExcl(e) {
    const id = e.currentTarget.dataset.exclId
    const st = this._s6
    st.excl_off = e.currentTarget.checked ? st.excl_off.filter(x => x !== id) : [...new Set([...st.excl_off, id])]
    this._paint6(); this._save6()
  }

  addExcl(e) {
    e.preventDefault()
    const input = this.element.querySelector("[data-s6='exclInput']")
    const text = input.value.trim()
    if (!text) { input.focus(); return }
    const st = this._s6
    st.excl_own = [...st.excl_own, text]
    const id = `own${st.excl_own.length - 1}`
    const list = this.element.querySelector("[data-s6='exlist']")
    list.querySelector("[data-s6='exNone']")?.remove()
    const lab = document.createElement("label")
    lab.className = "ex"
    lab.innerHTML = `<input type="checkbox" checked data-excl-id="${id}" data-own="1" data-action="change->precon-s456#toggleExcl"><span><span class="block"></span><span class="muted block"></span></span>`
    lab.querySelector(".block").textContent = text
    lab.querySelector(".muted").textContent = this._t("added_by_you")
    list.appendChild(lab)
    input.value = ""
    this._paint6(); this._save6()
    track("Precon Exclusion Added", { project_id: this.p?.projectIdValue })
  }

  toggleOthers(e) {
    const box = this.element.querySelector("[data-s6='others']")
    const open = box.classList.contains("hidden")
    box.classList.toggle("hidden", !open)
    e.currentTarget.textContent = open ? e.currentTarget.dataset.labelHide : e.currentTarget.dataset.labelShow
  }

  noteInput(e) { this._s6.note = e.currentTarget.value; this._paint6() }
  noteChange(e) { this._s6.note = e.currentTarget.value; this._save6() }

  toggleFile(e) {
    const id = String(e.currentTarget.dataset.fileId)
    const st = this._s6
    st.files_off = e.currentTarget.checked ? st.files_off.filter(x => x !== id) : [...new Set([...st.files_off, id])]
    this._paint6(); this._save6()
  }

  pickPackageFiles(e) { this._uploadNoteFiles(Array.from(e.currentTarget.files || []), "owner_package"); e.currentTarget.value = "" }

  _addPackageFiles(files) {
    const list = this.element.querySelector("[data-s6='files']")
    files.forEach(f => {
      const lab = document.createElement("label")
      lab.className = "ex"
      lab.dataset.fileId = f.id
      lab.innerHTML = `<input type="checkbox" checked data-file-id="${f.id}" data-action="change->precon-s456#toggleFile"><span><span class="block"></span><span class="muted block"><span data-meta></span> · <button class="linkbtn mute" type="button" data-file-id="${f.id}" data-action="click->precon-s456#removePackageFile"></button></span></span>`
      lab.querySelector("input").dataset.name = f.name
      lab.querySelector(".block").textContent = f.name
      lab.querySelector("[data-meta]").textContent = this._t("file_meta")
      lab.querySelector("button").textContent = this._t("remove")
      list.appendChild(lab)
    })
    this._paint6()
  }

  async removePackageFile(e) {
    e.preventDefault()
    const id = e.currentTarget.dataset.fileId
    const lab = this.element.querySelector(`[data-s6='files'] label[data-file-id='${id}']`)
    lab?.remove()
    this._paint6()
    try { await this.p._json("DELETE", `${this.base}/budget_files/${id}`); this._toast(this._t("file_removed")) } catch (err) { this._toast(err.message, "error") }
  }

  async downloadPdf(e) {
    e.preventDefault()
    const href = e.currentTarget.href
    const win = window.open("", "_blank")
    await this._flush6()
    if (win) win.location = href; else window.open(href, "_blank")
    track("Precon Owner Package Downloaded", { project_id: this.p?.projectIdValue })
  }

  sendPackage() {
    const email = this.element.querySelector("[data-field='owner_email']")?.value?.trim()
    if (!email) { this._toast(this._t("no_email"), "error"); this.element.querySelector("[data-field='owner_email']")?.focus(); return }
    const parts = 1 + this.element.querySelectorAll("[data-s6='files'] input[data-file-id]:checked").length
    this.p?._confirm(this._t("send_title"), this._t(parts === 1 ? "send_text_one" : "send_text", { email, count: parts }), this._t("send_btn"), async () => {
      await this._flush6()
      this._send("POST", "owner_package/send", {}, { ok: (d) => this._t(d.version ? "sent_version" : "sent", { email: d.sent_to || email }) })
    })
  }

  markWon() {
    this.p?._confirm(this._t("won_title"), this._t("won_text"), this._t("won_btn"), async () => {
      const data = await this._send("POST", "result", { result: "won" })
      if (data) { this._toast(data.message || this._t("won_toast")); setTimeout(() => { window.location.href = data.redirect_to }, 1200) }
    })
  }

  markLost() {
    this.p?._confirm(this._t("lost_title"), this._t("lost_text"), this._t("lost_btn"), () => this._send("POST", "result", { result: "lost" }, { ok: this._t("lost_toast") }))
  }
}
